import type { Container } from 'pixi.js';
import type { GameEventController } from '../types';
import { RaceEventIconView } from './views/RaceEventIconView';
import type { IObservableValue, MenuIcon } from '../../mainMenu/types';
import { openRaceEventPopup } from './behaviours/openRaceEventPopup';
import type { RaceEventState } from './types';
import type { MetaPopupController } from '../../metaAbstractions/types';
import type { PlatformUser } from '../../app/platform/Platform';
import type { ResourceMap } from '../../app/resources/types';
import type { EventCondition } from '../../app/state/events/types';
import { startMainMenuRaceEventFlow } from './behaviours/startMainMenuRaceEventFlow';
import { getRaceEventMinLevel, raceEventWinLevel } from './RaceEventUtils';
import { MS_IN_MINUTE } from '../../utils/time-utils';
import { preloadLayoutsAssets } from '../../views/preloadLayoutsAssets';
import { RaceEventIconLayout } from '../../../layouts/raceEvent/RaceEventIconLayout';
import { RaceEventPopup } from './views/RaceEventPopup';
import { RaceEventCarLayout } from '../../../layouts/raceEvent/RaceEventCarLayout';

export type RaceEventObservable = IObservableValue<{
  levelIndex: number;
  raceEvent: RaceEventState | null;
  raceEventLastEndTime: number;
}>;

export interface RaceEventDependencies {
  observable: RaceEventObservable;
  currentUser: PlatformUser;
  popupsController: MetaPopupController;
  shareImage: ((imageBase64: Promise<string>) => Promise<void>) | null;
  addResources: (resourceMap: ResourceMap, eventId: string) => void;
  isConditionMet: (condition: EventCondition) => boolean;
  tracking: {
    onEventStarted(eventId: string): void;
    onEventFinished(state: RaceEventState, finishedRank: number): void;
    onFirstInstancePopupOpened(): void;
  };
}

export class RaceEventController implements GameEventController {
  public readonly featureName = 'Race Event';

  private menuIcon: RaceEventIconView | null = null;

  private readonly rewardTargets: {
    coins: Container | null;
    boosters: Container | null;
  } = { coins: null, boosters: null };

  constructor(private readonly dependencies: RaceEventDependencies) {}

  public async startMainMenuFlow(): Promise<void> {
    await preloadLayoutsAssets([RaceEventIconLayout, RaceEventPopup, RaceEventCarLayout], true);

    if (!this.menuIcon) {
      console.warn('RaceEventController - missing menu icon');
      return;
    }

    if (!this.rewardTargets.coins || !this.rewardTargets.boosters) {
      console.warn('RaceEventController - missing reward targets ');
      return;
    }

    await startMainMenuRaceEventFlow({
      dependencies: this.dependencies,
      raceEventIcon: this.menuIcon,
      coinTarget: this.rewardTargets.coins,
      boosterTarget: this.rewardTargets.boosters,
    });
  }

  public setRewardTargets(coinsTarget: Container, boosterTarget: Container): void {
    this.rewardTargets.coins = coinsTarget;
    this.rewardTargets.boosters = boosterTarget;

    coinsTarget.once('destroyed', () => {
      if (this.rewardTargets.coins !== coinsTarget) return;
      this.rewardTargets.coins = null;
    });

    boosterTarget.once('destroyed', () => {
      if (this.rewardTargets.boosters !== boosterTarget) return;
      this.rewardTargets.boosters = null;
    });
  }

  public createGameEventIcon(): { icon: MenuIcon, unlockLevel: number } {
    const iconView = new RaceEventIconView({
      state: this.dependencies.observable,
    });

    iconView.onClicked.connect(() => {
      const { coins, boosters } = this.rewardTargets;
      if (coins && boosters) void openRaceEventPopup(this.dependencies, coins, boosters);
    });

    this.menuIcon = iconView;

    iconView.once('destroyed', () => {
      this.menuIcon = null;
    });

    return { icon: iconView, unlockLevel: getRaceEventMinLevel() };
  }

  public onLevelWon(): void {
    raceEventWinLevel(this.dependencies.observable);
  }

  public getDebugFunctions(): Record<string, () => void> {
    const { observable } = this.dependencies;

    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      'Advance time by 10 minutes': (): void => {
        const state = observable.getValue().raceEvent;
        if (!state) return;

        const diff = MS_IN_MINUTE * 10;

        observable.updateValue({
          raceEvent: {
            ...state,
            startDate: state.startDate !== null ? state.startDate - diff : null,
            joinedShowed: state.joinedShowed - diff,
          },
        });
      },
      'Increase player level': (): void => {
        raceEventWinLevel(observable);
      },
      'Cross finish line': (): void => {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          raceEventWinLevel(observable);
          const state = observable.getValue().raceEvent;
          if (!state || state.finished) return;
        }
      },
      'Clear state': (): void => {
        observable.updateValue({
          raceEvent: null,
          raceEventLastEndTime: 0,
        });
      },
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }
}
